import { LoaderCircle } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  addOrderHistory,
  addQuickReorderProduct,
} from "@/store/slices/appSlice";
import { QuickReorder as QuickReorderTypes } from "@/types/AppTypes";
import { generateClient } from "@/utils/helper/aws-generateClient-singleton";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { createOrders, updateQuickReorder } from "@/graphql/mutations";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import QuickReorderTableWrapper from "@/components/quickReorderCompo/QuickReorderTableWrapper";

export default function QuickReorder({ className }: { className?: string }) {
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const client = generateClient();
  const [stockLoading, setStockLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reorderLoading, setReorderLoading] = useState(false);

  const handleStockChange = useCallback(
    async (product: QuickReorderTypes, newStock: number) => {
      const updatedQuickReorder = await executeGraphQLMutation({
        query: updateQuickReorder,
        variables: {
          input: {
            id: product?.id,
            stock: newStock,
            updated_at: new Date().toISOString(),
          },
        },
        setLoading: setStockLoading,
      });
      dispatch(addQuickReorderProduct(updatedQuickReorder));
    },
    [dispatch]
  );

  const handleReorderProduct = useCallback(
    async (product: QuickReorderTypes, quantity: number) => {
      setReorderLoading(true);
      try {
        const order = await executeGraphQLMutation({
          query: createOrders,
          variables: {
            input: {
              id: uuidv4(),
              user_id: user?.user_id,
              product_id: product?.product_id,
              quantity: quantity,
              seller_id: product?.seller_id,
              brand_id: product?.brand_id,
              category_id: product?.category_id,
              status: "PENDING",
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              variant:
                typeof product?.variant === "string"
                  ? product?.variant
                  : JSON.stringify(product?.variant),
            },
          },
          client,
        });

        if (order) {
          const updatedQuickReorder = await executeGraphQLMutation({
            query: updateQuickReorder,
            variables: {
              input: {
                id: product?.id,
                stock: (product?.stock ?? 0) + quantity,
                updated_at: new Date().toISOString(),
              },
            },
          });

          if (updatedQuickReorder) {
            toast.success("Product has been Reordered Successfully");
            dispatch(addQuickReorderProduct(updatedQuickReorder));
            dispatch(addOrderHistory(order));
          }
        }
      } catch (error) {
        console.error("Error reordering product:", error);
      } finally {
        setReorderLoading(false);
        setDialogOpen(false);
      }
    },
    [user, dispatch, client]
  );

  return (
    <QuickReorderTableWrapper
      handleReorderProduct={handleReorderProduct}
      handleStockUpdate={handleStockChange}
      stockLoading={stockLoading}
      dialogOpen={dialogOpen}
      setDialogOpen={setDialogOpen}
      reorderLoading={reorderLoading}
      className={className}
    />
  );
}
